"use client"

import { Input } from "@/components/ui/input"
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { Search, Filter } from "lucide-react"
import type React from "react"

interface BlogCategory {
    blog_category: string
}

interface BlogLeftSideProps {
    searchTerm: string
    onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    blogCategories: BlogCategory[]
    selectedCategory: string | undefined
    setSelectedCategory: (category: string | undefined) => void
    page: number
    setPage: (page: number) => void
}

const BlogLeftSide = ({
    searchTerm,
    onSearchChange,
    blogCategories,
    selectedCategory,
    setSelectedCategory,
    page,
    setPage,
}: BlogLeftSideProps) => {
    return (
        <aside className="flex flex-col gap-6 w-full sm:max-w-xs bg-card rounded-xl p-6 border shadow-sm">
            {/* Header Section */}
            <header className="flex flex-col gap-4">
                <div className="flex items-center gap-2">
                    <h2 className="text-2xl font-bold text-foreground">Blog Explorer</h2>
                </div>
                <p className="text-sm text-muted-foreground">Discover articles and insights</p>
            </header>

            <Separator />

            {/* Search Section */}
            <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                    <Search className="h-4 w-4" />
                    <span>Search</span>
                </div>
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        type="text"
                        placeholder="Search blogs by title..."
                        className="pl-10 h-11 bg-background border-input focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                        value={searchTerm}
                        onChange={onSearchChange}
                    />
                </div>
            </div>

            {/* Category Filter Section */}
            <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                    <Filter className="h-4 w-4" />
                    <span>Category</span>
                </div>
                <Select
                    value={selectedCategory || "all"}
                    onValueChange={(value) => {
                        setSelectedCategory(value === "all" ? undefined : value)
                        setPage(1)
                    }}
                >
                    <SelectTrigger  aria-label="Select category" className="h-11 bg-background">
                        <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem aria-label="all categories will be shown" value="all" className="font-medium">
                            All Categories
                        </SelectItem>
                        {blogCategories.map((cat) => (
                            <SelectItem aria-label={cat.blog_category} key={cat.blog_category} value={cat.blog_category} className="capitalize">
                                {cat.blog_category}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {/* Stats Section */}
            <div className="pt-2">
                <div className="text-xs text-muted-foreground">
                    {selectedCategory ? (
                        <span>
                            Filtered by: <span className="font-medium text-foreground">{selectedCategory}</span>
                        </span>
                    ) : (
                        <span>Showing all categories</span>
                    )}
                </div>
            </div>
        </aside>
    )
}

export default BlogLeftSide

